<template>
    <div class="horse-edit-main-dates flex gap-3 mb-3">
        <div class="date">
            <div class="label">
                {{requiredForNotExternalHorse ? '*' : ''}}
                {{$t('message.horseEdit.main.dates.birth')}}:
            </div>
            <CustomCalendar name="dateOfBirth" v-model="horse.dateOfBirth" class="w-full"
                            :rules="requiredForNotExternalHorse ? 'required' : ''"
                            :disabled="disableEdit" :max-date="today"/>
            <div class="flex gap-2 align-items-center w-full">
                <span class="addition-information-label">{{$t('message.horseEdit.main.dates.in')}}:</span>
                <CustomSelectOneCountry name="countryOfBirthId" v-model="horse.countryOfBirthId" noPlaceholder class="w-full"
                                        :rules="requiredForNotExternalHorse ? 'required' : ''"
                                        itemValue="id" :disabled="disableEdit"/>
            </div>
        </div>
        <div class="date" v-if="imported || horse?.imported">
            <div class="label">
                {{requiredForImported && requiredForNotExternalHorse ? '*' : ''}}
                {{$t('message.horseEdit.main.dates.import')}}:
            </div>
            <CustomCalendar name="dateOfImport" v-model="horse.dateOfImport" class="w-full"
                            :rules="requiredForImported && requiredForNotExternalHorse ? 'required' : ''"
                            :disabled="disableEdit" :max-date="today"/>
            <div class="relative">
                <CustomValidationMessage v-if="dateOfImportValidationMessage" errorMessage="Date of import cannot be earlier than date of birth"/>
            </div>
            <div class="flex gap-2 align-items-center w-full">
                <span class="addition-information-label">{{$t('message.horseEdit.main.dates.from')}}:</span>
                <CustomSelectOneCountry name="countryOfImportId" v-model="horse.countryOfImportId" noPlaceholder class="w-full"
                                        itemValue="id" :disabled="disableEdit"
                                        :rules="requiredForImported && requiredForNotExternalHorse ? 'required' : ''" />
            </div>
        </div>
        <div class="date">
            <div class="label">
                {{requiredForNotExternalHorse || horse.imported ? '*' : ''}}
                {{$t('message.horseEdit.main.dates.control')}}:
            </div>
            <CustomCalendar name="dateOfControl" v-model="horse.dateOfControl" class="w-full"
                            :rules="requiredForNotExternalHorse ? 'required' : ''"
                            :disabled="disableEdit" :max-date="today"/>
            <div class="relative">
                <CustomValidationMessage v-if="dateOfControlValidationMessage" errorMessage="Date of control cannot be earlier than date of birth" />
            </div>
            <div class="flex gap-2 align-items-center w-full">
                <span class="addition-information-label">{{$t('message.horseEdit.main.dates.by')}}:</span>
                <CustomAutocompleteVeterinarian name="controlledBy" v-model="horse.controlledBy" search-param="fullName"
                                                :rules="requiredForNotExternalHorse ? 'required' : ''"
                                                :disabled="disableEdit"/>
            </div>
        </div>
        <div class="date" v-if="horse.id && !imported">
            <div class="label">
                <i class="pi pi-question-circle" v-tooltip.top="$t('message.horseEdit.main.dates.exportDisclaimer')"></i>
                {{$t('message.horseEdit.main.dates.export')}}:
            </div>
            <CustomCalendar name="dateOfExport" v-model="horse.dateOfExport" class="w-full" disabled/>
            <div class="relative">
                <CustomValidationMessage v-if="dateOfExportValidationMessage" errorMessage="Date of export cannot be earlier than date of birth" />
            </div>
            <div class="flex gap-2 align-items-center w-full">
                <span class="addition-information-label">{{$t('message.horseEdit.main.dates.to')}}:</span>
                <CustomSelectOneCountry name="countryOfExportId" v-model="horse.countryOfExportId" noPlaceholder class="w-full"
                                        itemValue="id" disabled/>
            </div>
        </div>
        <div class="date" v-if="horse.id && !imported">
            <div class="label">
                <i class="pi pi-question-circle" v-tooltip.top="$t('message.horseEdit.main.dates.deathDisclaimer')"></i>
                {{$t('message.horseEdit.main.dates.death')}}:
            </div>
            <CustomCalendar name="dateOfDeath" v-model="horse.dateOfDeath" class="w-full" disabled/>
            <div class="relative">
                <CustomValidationMessage v-if="dateOfDeathValidationMessage" errorMessage="Date of death cannot be earlier than date of birth" />
            </div>
            <div class="flex gap-2 align-items-center w-full">
            </div>
        </div>
    </div>
</template>

<script>

import CustomSelectOneCountry from "@/components/form/CustomSelectOneCountry.vue";
import CustomCalendar from "@/components/form/CustomCalendar.vue";
import CustomAutocompleteUser from "@/components/form/CustomAutocompleteUser.vue";
import CustomAutocompleteVeterinarian from "@/components/form/CustomAutocompleteVeterinarian.vue";
import Tooltip from "primevue/tooltip";
import {HorseStatus} from "@/utils/HorseStatus";
import CustomValidationMessage from "@/components/form/inner/CustomValidationMessage.vue";

export default {
    name: "HorseEditMainDates",
    components: {
        CustomValidationMessage,
        CustomAutocompleteVeterinarian,
        CustomAutocompleteUser,
        CustomCalendar,
        CustomSelectOneCountry,
    },

    props: {
        modelValue: {
            type: Object,
            required: true,
        },
        validationErrors: {
            type: Object,
            default: {},
        },
        disableEdit: {
            type: Boolean,
            default: false,
        },
        imported: {
            type: Boolean,
            default: false,
        },
    },

    directives: {
        tooltip: Tooltip,
    },

    emits: ["update:modelValue"],

    computed: {
        horse: {
            get() { return this.modelValue; },
            set(value) { this.$emit("update:modelValue", value); },
        },
        requiredForNotExternalHorse() {
            return this?.horse?.horseStatus !== HorseStatus.EXTERNAL;
        },
        requiredForImported() {
            return this?.horse?.imported || this.imported;
        },
        dateOfImportValidationMessage() {
            return !this.validateDateOfImport();
        },
        dateOfControlValidationMessage() {
            return !this.validateDateOfControl();
        },
        dateOfExportValidationMessage() {
            return !this.validateDateOfExport();
        },
        dateOfDeathValidationMessage() {
            return !this.validateDateOfDeath();
        },
        today() {
            return new Date();
        }
    },

    methods: {
        validateDateOfImport() {
            if (this.horse.dateOfBirth !== null && this.horse.dateOfImport !== null) {
                return this.parseDMY(this.horse.dateOfBirth) <= this.parseDMY(this.horse.dateOfImport);
            } else {
                return true;
            }
        },
        validateDateOfControl() {
            if (this.horse.dateOfBirth !== null && this.horse.dateOfControl !== null) {
                return this.parseDMY(this.horse.dateOfBirth) <= this.parseDMY(this.horse.dateOfControl);
            } else {
                return true;
            }
        },
        validateDateOfExport() {
            if (this.horse.dateOfBirth !== null && this.horse.dateOfExport !== null) {
                return this.parseDMY(this.horse.dateOfBirth) <= this.parseDMY(this.horse.dateOfExport);
            } else {
                return true;
            }
        },
        validateDateOfDeath() {
            if (this.horse.dateOfBirth !== null && this.horse.dateOfDeath !== null) {
                return this.parseDMY(this.horse.dateOfBirth) <= this.parseDMY(this.horse.dateOfDeath);
            } else {
                return true;
            }
        },
        parseDMY(dateStr) {
            const [day, month, year] = dateStr.split('-');
            return new Date(`${year}-${month}-${day}`);
        },
        getRequiredMessage(value) {
            if (value === null || value === "") {
                return this.$t("message.validations.required");
            }
            return "";
        },
    },
};
</script>

<style lang="scss">
@import "../../../../../assets/theme/mytheme/variables";
.imported-horse-form {
    .horse-edit-main-dates {
        .date {
            max-width: unset;
        }
    }
}
.horse-edit-main-dates {
    flex-wrap: wrap;
    justify-content: center;
    .date {
        display: flex;
        flex-direction: column;
        align-items: center;
        background: var(--dates-background-color);
        color: var(--dates-text-color);
        border-radius: 7px;
        padding: 0.7rem;
        // width: 40%;
        max-width: 290px;
        text-align: center;
        gap: 1rem;
        flex: 1 1 calc(33.333% - 1.5rem);
        min-width: 250px;

        .label {
            font-size: 18px;
            text-transform: uppercase;
        }
        .addition-information-label {
            font-size: 18px;
            text-transform: uppercase;
        }
        .custom-select-one .p-dropdown {
            border-color: var(--dates-text-color);
            .p-icon {
                color: var(--dates-text-color);
            }
        }
        .custom-select-one .p-dropdown-label {
            color: var(--dates-text-color);
        }
        .custom-autocomplete {
            .p-autocomplete-input {
                color: var(--dates-text-color);
                border-color: var(--dates-text-color);
            }
            .p-button {
                background: none;
                color: var(--dates-text-color);
                border-color: var(--dates-text-color);
                border-left: 0;
            }
        }
        .p-calendar .p-inputtext {
            color: var(--dates-text-color);
            border-color: var(--dates-text-color);
        }
        .p-calendar .p-inputtext.p-placeholder {
            color: var(--dates-text-color);
        }
    }
}
</style>
